import { Badge } from '@/components/ui/badge'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Skeleton, SkeletonRows } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { type AgentRunParams, useAgentRunErrors } from '@/models/agentRunReport'
import { formatCount, formatDateTime, formatPercent } from './format'

export function ErrorsTab({
  params,
  onInspectRun,
}: {
  params: AgentRunParams
  onInspectRun: (runId: string) => void
}) {
  const { data, isLoading } = useAgentRunErrors(params)

  if (isLoading || !data) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-10 w-full" />
        <SkeletonRows rows={6} />
      </div>
    )
  }

  if (!data.groups.length) {
    return <p className="text-ink-400 py-12 text-center text-sm">No failures in this range.</p>
  }

  return (
    <div className="flex flex-col gap-3">
      {data.truncated && (
        <Banner variant="warning">
          Too many failures to group them all — showing the most recent {formatCount(data.scanned)}. Narrow
          the date range for exact counts.
        </Banner>
      )}
      <p className="text-ink-400 text-sm">
        Grouped by what went wrong, with ids and counters stripped. Cancellations are excluded.
      </p>
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-48 font-semibold">Agent</TableHead>
            <TableHead className="font-semibold">Failure</TableHead>
            <TableHead className="w-20 text-right font-semibold">Count</TableHead>
            <TableHead className="w-24 text-right font-semibold">Of runs</TableHead>
            <TableHead className="w-32 font-semibold">First seen</TableHead>
            <TableHead className="w-32 font-semibold">Last seen</TableHead>
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.groups.map((group) => (
            <TableRow key={`${group.agentSlug}-${group.signature}`}>
              <TableCell className="truncate align-top font-mono text-sm">{group.agentSlug}</TableCell>
              {/* TableCell defaults to whitespace-nowrap, which a failure message needs off. */}
              <TableCell className="align-top whitespace-normal">
                <p className="line-clamp-3 text-sm break-words" title={group.sample}>
                  {group.signature}
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {group.models.map((model) => (
                    <Badge key={model} variant="outline">
                      {model}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-right align-top tabular-nums">{formatCount(group.count)}</TableCell>
              <TableCell className="text-right align-top tabular-nums">
                {formatPercent(group.shareOfAgentRuns, 0)}
                <div className="text-ink-400 text-xs">of {formatCount(group.agentRuns)}</div>
              </TableCell>
              <TableCell className="text-ink-500 align-top text-sm whitespace-nowrap">
                {formatDateTime(group.firstSeen)}
              </TableCell>
              <TableCell className="text-ink-500 align-top text-sm whitespace-nowrap">
                {formatDateTime(group.lastSeen)}
              </TableCell>
              <TableCell className="align-top">
                <Button variant="outline" size="sm" onClick={() => onInspectRun(group.lastRunId)}>
                  Inspect
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
